import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { FaEllipsisV, FaShieldAlt } from "react-icons/fa";

export const CREDENTIAL_OPTIONS = [
  "LCSW – Licensed Clinical Social Worker",
  "LMSW – Licensed Master Social Worker",
  "LSW – Licensed Social Worker",
  "LICSW – Licensed Independent Clinical Social Worker",
  "LCSW-C – Licensed Certified Social Worker–Clinical",
  "LGSW – Licensed Graduate Social Worker",
  "LPC – Licensed Professional Counselor",
  "LPCC – Licensed Professional Clinical Counselor",
  "LCPC – Licensed Clinical Professional Counselor",
  "LMHC – Licensed Mental Health Counselor",
  "LPC-A – Licensed Professional Counselor Associate",
  "LPCA – Licensed Professional Counselor Associate",
  "LMFT – Licensed Marriage and Family Therapist",
  "LAMFT – Licensed Associate Marriage and Family Therapist",
  "MFT-A – Marriage and Family Therapist Associate",
  "PhD – Licensed Psychologist",
  "PsyD – Licensed Psychologist",
  "MD – Psychiatrist",
  "PMHNP – Psychiatric Mental Health Nurse Practitioner",
  "APRN – Advanced Practice Registered Nurse",
];

const STATE_ABBR = {
  Alabama: "AL",
  Alaska: "AK",
  Arizona: "AZ",
  Arkansas: "AR",
  California: "CA",
  Colorado: "CO",
  Connecticut: "CT",
  Delaware: "DE",
  Florida: "FL",
  Georgia: "GA",
  Hawaii: "HI",
  Idaho: "ID",
  Illinois: "IL",
  Indiana: "IN",
  Iowa: "IA",
  Kansas: "KS",
  Kentucky: "KY",
  Louisiana: "LA",
  Maine: "ME",
  Maryland: "MD",
  Massachusetts: "MA",
  Michigan: "MI",
  Minnesota: "MN",
  Mississippi: "MS",
  Missouri: "MO",
  Montana: "MT",
  Nebraska: "NE",
  Nevada: "NV",
  "New Hampshire": "NH",
  "New Jersey": "NJ",
  "New Mexico": "NM",
  "New York": "NY",
  "North Carolina": "NC",
  "North Dakota": "ND",
  Ohio: "OH",
  Oklahoma: "OK",
  Oregon: "OR",
  Pennsylvania: "PA",
  "Rhode Island": "RI",
  "South Carolina": "SC",
  "South Dakota": "SD",
  Tennessee: "TN",
  Texas: "TX",
  Utah: "UT",
  Vermont: "VT",
  Virginia: "VA",
  Washington: "WA",
  "West Virginia": "WV",
  Wisconsin: "WI",
  Wyoming: "WY",
  "District of Columbia": "DC",
};

const emptyLicense = () => ({
  state: "",
  credentials: "",
  licenseNumber: "",
  licenseExpiration: "",
  status: "Active",
});

const toStateCode = (state) => {
  if (!state) return "—";
  if (STATE_ABBR[state]) return STATE_ABBR[state];
  if (String(state).length <= 3) return String(state).toUpperCase();
  return String(state);
};

const formatDisplayDate = (value) => {
  if (!value) return "—";
  const raw = String(value).slice(0, 10);
  const [y, m, d] = raw.split("-");
  if (y && m && d) return `${m}/${d}/${y}`;
  return value;
};

const todayIso = () => new Date().toISOString().slice(0, 10);

const resolveLicenseStatus = (license) => {
  const stored = license?.status || "Active";
  if (stored === "Inactive" || stored === "Pending Review") return stored;
  if (
    license?.licenseExpiration &&
    String(license.licenseExpiration).slice(0, 10) < todayIso()
  ) {
    return "Expired";
  }
  return stored === "Expired" ? "Expired" : "Active";
};

const normalizeLicenses = (credentials) => {
  if (!credentials) return [];
  if (typeof credentials === "string") {
    if (credentials.includes("[object Object]")) return [];
    try {
      const parsed = JSON.parse(credentials);
      return normalizeLicenses(parsed);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(credentials)) return [];
  return credentials
    .filter((item) => item && typeof item === "object" && !Array.isArray(item))
    .map((item) => ({
      state: item.state || "",
      credentials: item.credentials || item.credential || "",
      licenseNumber: item.licenseNumber || "",
      licenseExpiration: item.licenseExpiration
        ? String(item.licenseExpiration).slice(0, 10)
        : "",
      status: item.status || "Active",
    }));
};

const StatusBadge = ({ status }) => {
  const tone =
    status === "Active"
      ? "active"
      : status === "Expired"
        ? "expired"
        : status === "Pending Review"
          ? "pending"
          : "inactive";
  return <span className={`pp-license-status pp-license-status-${tone}`}>{status}</span>;
};

const ProviderCredentialsTab = ({
  provider,
  getProviderId,
  onProviderUpdated,
  fetchedStates = [],
  allowHardRemove = false,
}) => {
  const [editingNpi, setEditingNpi] = useState(false);
  const [npiDraft, setNpiDraft] = useState(provider?.npiNumber || "");
  const [npiError, setNpiError] = useState("");
  const [savingNpi, setSavingNpi] = useState(false);

  const [licenses, setLicenses] = useState(() =>
    normalizeLicenses(provider?.credentials),
  );
  const [savingLicenses, setSavingLicenses] = useState(false);
  const [licenseModal, setLicenseModal] = useState(null); // { mode: 'add'|'edit', index, draft }
  const [licenseErrors, setLicenseErrors] = useState({});
  const [menuOpenIndex, setMenuOpenIndex] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!editingNpi) setNpiDraft(provider?.npiNumber || "");
  }, [provider?.npiNumber, editingNpi]);

  useEffect(() => {
    setLicenses(normalizeLicenses(provider?.credentials));
  }, [provider?.credentials]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpenIndex(null);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const persistProvider = async (payload) => {
    const request = await axios.post(
      `${process.env.REACT_APP_BASE_URL}/specialist/editprovider/${getProviderId()}`,
      {
        email: provider.email,
        firstname: provider.firstname,
        lastname: provider.lastname,
        providerType: provider.providerType || "Therapist",
        yearsOfExperience: provider.yearsOfExperience,
        specialisation: provider.specialisation,
        ...(Array.isArray(provider.credentials)
          ? { credentials: provider.credentials }
          : {}),
        npiNumber: provider.npiNumber,
        ...payload,
      },
    );
    return request;
  };

  const startEditNpi = () => {
    setNpiDraft(provider?.npiNumber || "");
    setNpiError("");
    setEditingNpi(true);
  };

  const cancelEditNpi = () => {
    setNpiDraft(provider?.npiNumber || "");
    setNpiError("");
    setEditingNpi(false);
  };

  const saveNpi = async () => {
    const digits = String(npiDraft || "").replace(/\D/g, "");
    if (!digits) {
      setNpiError("NPI Number is required.");
      return;
    }
    if (digits.length !== 10) {
      setNpiError("NPI Number must be exactly 10 digits.");
      return;
    }

    try {
      setSavingNpi(true);
      const request = await persistProvider({ npiNumber: digits });
      if (request.data.success) {
        onProviderUpdated({
          ...provider,
          ...(request.data.data || {}),
          npiNumber: digits,
        });
        setEditingNpi(false);
        toast.success("NPI Number updated successfully.");
      } else {
        toast.error(request.data.message || "Failed to update NPI Number.");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to update NPI Number.",
      );
    } finally {
      setSavingNpi(false);
    }
  };

  const openAddLicense = () => {
    setLicenseErrors({});
    setLicenseModal({ mode: "add", index: -1, draft: emptyLicense() });
    setMenuOpenIndex(null);
  };

  const openEditLicense = (index) => {
    setLicenseErrors({});
    setLicenseModal({
      mode: "edit",
      index,
      draft: { ...licenses[index] },
    });
    setMenuOpenIndex(null);
  };

  const closeLicenseModal = () => {
    setLicenseModal(null);
    setLicenseErrors({});
  };

  const updateLicenseDraft = (field, value) => {
    if (field === "licenseExpiration" && value) {
      const year = value.split("-")[0];
      if (year.length > 4) return;
    }
    setLicenseModal((prev) =>
      prev
        ? {
            ...prev,
            draft: { ...prev.draft, [field]: value },
          }
        : prev,
    );
  };

  const validateLicenseDraft = (draft, mode, editIndex) => {
    const errors = {};
    if (!draft.state) errors.state = "State is required.";
    if (!draft.credentials) errors.credentials = "License Type is required.";
    if (!draft.licenseNumber?.trim()) {
      errors.licenseNumber = "License Number is required.";
    }
    if (!draft.licenseExpiration) {
      errors.licenseExpiration = "Expiration Date is required.";
    } else if (String(draft.licenseExpiration).slice(0, 10) < todayIso()) {
      errors.licenseExpiration = "Expiration Date cannot be in the past.";
    }

    if (draft.state && draft.credentials) {
      const duplicate = licenses.some((item, index) => {
        if (mode === "edit" && index === editIndex) return false;
        const itemStatus = resolveLicenseStatus(item);
        return (
          itemStatus === "Active" &&
          item.state === draft.state &&
          item.credentials === draft.credentials
        );
      });
      if (duplicate) {
        errors.credentials =
          "An active license already exists for this state and credential.";
      }
    }

    return errors;
  };

  const saveLicenses = async (nextLicenses, successMessage) => {
    try {
      setSavingLicenses(true);
      const request = await persistProvider({ credentials: nextLicenses });
      if (request.data.success) {
        setLicenses(nextLicenses);
        onProviderUpdated({
          ...provider,
          ...(request.data.data || {}),
          credentials: nextLicenses,
        });
        toast.success(successMessage);
        return true;
      }
      toast.error(request.data.message || "Failed to update licenses.");
      return false;
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to update licenses.",
      );
      return false;
    } finally {
      setSavingLicenses(false);
    }
  };

  const submitLicenseDrawer = async () => {
    if (!licenseModal) return;
    const errors = validateLicenseDraft(
      licenseModal.draft,
      licenseModal.mode,
      licenseModal.index,
    );
    setLicenseErrors(errors);
    if (Object.keys(errors).length) {
      toast.error("Please fix the highlighted fields and try again.");
      return;
    }

    const draft = {
      ...licenseModal.draft,
      licenseNumber: licenseModal.draft.licenseNumber.trim(),
      status: licenseModal.draft.status || "Active",
    };

    let next;
    if (licenseModal.mode === "add") {
      next = [...licenses, draft];
    } else {
      next = licenses.map((item, i) =>
        i === licenseModal.index ? draft : item,
      );
    }

    const ok = await saveLicenses(
      next,
      licenseModal.mode === "add"
        ? "License added successfully."
        : "License updated successfully.",
    );
    if (ok) closeLicenseModal();
  };

  const deactivateLicense = async (index) => {
    setMenuOpenIndex(null);
    const next = licenses.map((item, i) =>
      i === index ? { ...item, status: "Inactive" } : item,
    );
    await saveLicenses(next, "License deactivated.");
  };

  const removeLicense = async (index) => {
    setMenuOpenIndex(null);
    if (
      !window.confirm(
        "Remove this license? You can add it again later if needed.",
      )
    ) {
      return;
    }
    const next = licenses.filter((_, i) => i !== index);
    await saveLicenses(next, "License removed.");
  };

  const npiValue = provider?.npiNumber || "";

  return (
    <div className="pp-credentials-tab">
      {/* NPI Number */}
      {editingNpi ? (
        <div className="pp-card">
          <div className="pp-card-header">
            <div>
              <h3 className="pp-card-title">NPI Number</h3>
              <p className="pp-card-subtitle">
                National Provider Identifier – used for insurance billing.
              </p>
            </div>
          </div>
          <div className="pp-card-body">
            <div className="pp-form-field" style={{ maxWidth: 360 }}>
              <label className="pp-info-label">NPI Number</label>
              <input
                className={`pp-form-input ${npiError ? "pp-form-input-error" : ""}`}
                value={npiDraft}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
                  setNpiDraft(digits);
                  setNpiError("");
                }}
                placeholder="10 digit NPI"
                inputMode="numeric"
                maxLength={10}
              />
              {npiError && <span className="pp-field-error">{npiError}</span>}
            </div>
            <div className="pp-card-footer">
              <span className="pp-unsaved-label">Unsaved changes</span>
              <div className="pp-card-footer-actions pp-npi-footer-actions">
                <button
                  type="button"
                  className="pp-btn-cancel"
                  onClick={cancelEditNpi}
                  disabled={savingNpi}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="pp-btn-save"
                  onClick={saveNpi}
                  disabled={savingNpi}
                >
                  {savingNpi ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="pp-section">
          <div className="pp-section-header">
            <div>
              <h3>NPI Number</h3>
              <p className="pp-section-header-subtitle">
                National Provider Identifier – used for insurance billing.
              </p>
            </div>
            <div className="pp-section-header-actions">
              <button
                type="button"
                className="pp-section-edit pp-credentials-header-btn"
                onClick={startEditNpi}
              >
                Edit
              </button>
            </div>
          </div>
          <div className="pp-section-body">
            {npiValue ? (
              <div className="pp-npi-display">
                <FaShieldAlt className="pp-npi-icon" />
                <span className="pp-npi-value">{npiValue}</span>
              </div>
            ) : (
              <div className="pp-credentials-empty">
                <p className="pp-muted">No NPI number added yet.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* State Licenses */}
      <div className="pp-section">
        <div className="pp-section-header">
          <div>
            <h3>State Licenses</h3>
            <p className="pp-section-header-subtitle">
              States where this provider is licensed to practice.
            </p>
          </div>
          <div className="pp-section-header-actions">
            <button
              type="button"
              className="pp-section-edit pp-credentials-header-btn"
              onClick={openAddLicense}
              disabled={savingLicenses}
            >
              + Add License
            </button>
          </div>
        </div>
        <div
          className={`pp-section-body ${
            licenses.length ? "pp-licenses-body" : ""
          }`}
        >
          {!licenses.length ? (
            <div className="pp-credentials-empty">
              <p className="pp-muted">No licenses added yet.</p>
              <button
                type="button"
                className="pp-section-edit pp-credentials-header-btn"
                onClick={openAddLicense}
                disabled={savingLicenses}
                style={{ marginTop: 12 }}
              >
                + Add License
              </button>
            </div>
          ) : (
            <div className="pp-licenses-table-wrap">
              <table className="pp-licenses-table">
                <thead>
                  <tr>
                    <th>State</th>
                    <th>License Number</th>
                    <th>License Type</th>
                    <th>Expiration Date</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {licenses.map((license, index) => {
                    const status = resolveLicenseStatus(license);
                    return (
                      <tr key={`${license.state}-${license.licenseNumber}-${index}`}>
                        <td>
                          <span className="pp-state-badge">
                            {toStateCode(license.state)}
                          </span>
                        </td>
                        <td>{license.licenseNumber || "—"}</td>
                        <td>{license.credentials || "—"}</td>
                        <td>{formatDisplayDate(license.licenseExpiration)}</td>
                        <td>
                          <StatusBadge status={status} />
                        </td>
                        <td className="pp-license-actions-cell">
                          <div
                            className="pp-license-menu-wrap"
                            ref={menuOpenIndex === index ? menuRef : null}
                          >
                            <button
                              type="button"
                              className="pp-license-menu-btn"
                              aria-label="License actions"
                              onClick={() =>
                                setMenuOpenIndex(
                                  menuOpenIndex === index ? null : index,
                                )
                              }
                            >
                              <FaEllipsisV size={14} />
                            </button>
                            {menuOpenIndex === index && (
                              <div className="pp-license-menu">
                                <button
                                  type="button"
                                  onClick={() => openEditLicense(index)}
                                >
                                  Edit License
                                </button>
                                {allowHardRemove ? (
                                  <button
                                    type="button"
                                    className="pp-license-menu-danger"
                                    onClick={() => removeLicense(index)}
                                    disabled={savingLicenses}
                                  >
                                    Remove License
                                  </button>
                                ) : (
                                  status !== "Inactive" && (
                                    <button
                                      type="button"
                                      className="pp-license-menu-danger"
                                      onClick={() => deactivateLicense(index)}
                                      disabled={savingLicenses}
                                    >
                                      Deactivate License
                                    </button>
                                  )
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {licenseModal && (
        <div
          className="pp-license-drawer-overlay"
          onClick={closeLicenseModal}
        >
          <aside
            className="pp-license-drawer"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pp-license-drawer-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pp-license-drawer-header">
              <h2 id="pp-license-drawer-title">
                {licenseModal.mode === "add" ? "Add License" : "Edit License"}
              </h2>
              <p>
                {licenseModal.mode === "add"
                  ? "Add a new state license for this provider."
                  : "Update the license details below."}
              </p>
            </div>

            <div className="pp-license-drawer-body">
              <div className="pp-form-field">
                <label className="pp-info-label">
                  State <span className="pp-required">*</span>
                </label>
                <select
                  className={`pp-form-input pp-form-select ${
                    licenseErrors.state ? "pp-form-input-error" : ""
                  }`}
                  value={licenseModal.draft.state}
                  onChange={(e) => {
                    updateLicenseDraft("state", e.target.value);
                    setLicenseErrors((prev) => ({ ...prev, state: undefined }));
                  }}
                >
                  <option value="">Select state</option>
                  {fetchedStates.map((opt) => (
                    <option key={opt._id || opt.name} value={opt.name}>
                      {opt.name}
                    </option>
                  ))}
                </select>
                {licenseErrors.state && (
                  <span className="pp-field-error">{licenseErrors.state}</span>
                )}
              </div>

              <div className="pp-form-field">
                <label className="pp-info-label">
                  License Number <span className="pp-required">*</span>
                </label>
                <input
                  className={`pp-form-input ${
                    licenseErrors.licenseNumber ? "pp-form-input-error" : ""
                  }`}
                  value={licenseModal.draft.licenseNumber}
                  onChange={(e) => {
                    updateLicenseDraft("licenseNumber", e.target.value);
                    setLicenseErrors((prev) => ({
                      ...prev,
                      licenseNumber: undefined,
                    }));
                  }}
                  placeholder="e.g. LPC-23451"
                />
                {licenseErrors.licenseNumber && (
                  <span className="pp-field-error">
                    {licenseErrors.licenseNumber}
                  </span>
                )}
              </div>

              <div className="pp-form-field">
                <label className="pp-info-label">
                  License Type <span className="pp-required">*</span>
                </label>
                <select
                  className={`pp-form-input pp-form-select ${
                    licenseErrors.credentials ? "pp-form-input-error" : ""
                  }`}
                  value={licenseModal.draft.credentials}
                  onChange={(e) => {
                    updateLicenseDraft("credentials", e.target.value);
                    setLicenseErrors((prev) => ({
                      ...prev,
                      credentials: undefined,
                    }));
                  }}
                >
                  <option value="">Select type</option>
                  {CREDENTIAL_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
                {licenseErrors.credentials && (
                  <span className="pp-field-error">
                    {licenseErrors.credentials}
                  </span>
                )}
              </div>

              <div className="pp-form-field">
                <label className="pp-info-label">
                  Expiration Date <span className="pp-required">*</span>
                </label>
                <input
                  type="date"
                  className={`pp-form-input ${
                    licenseErrors.licenseExpiration
                      ? "pp-form-input-error"
                      : ""
                  }`}
                  value={licenseModal.draft.licenseExpiration}
                  min={todayIso()}
                  onChange={(e) => {
                    updateLicenseDraft("licenseExpiration", e.target.value);
                    setLicenseErrors((prev) => ({
                      ...prev,
                      licenseExpiration: undefined,
                    }));
                  }}
                />
                {licenseErrors.licenseExpiration && (
                  <span className="pp-field-error">
                    {licenseErrors.licenseExpiration}
                  </span>
                )}
              </div>
            </div>

            <div className="pp-license-drawer-footer">
              <button
                type="button"
                className="pp-btn-cancel"
                onClick={closeLicenseModal}
                disabled={savingLicenses}
              >
                Cancel
              </button>
              <button
                type="button"
                className="pp-btn-save"
                onClick={submitLicenseDrawer}
                disabled={savingLicenses}
              >
                {savingLicenses
                  ? "Saving..."
                  : licenseModal.mode === "add"
                    ? "Add License"
                    : "Save"}
              </button>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};

export default ProviderCredentialsTab;
